import TextInput from 'components/Common/Form/Input/TextInput'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import { useMutation } from '@tanstack/react-query'
import { checkUserExistsForBrand } from 'api/auth'
import ReCAPTCHA from 'react-google-recaptcha'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import styled from 'styled-components'
import config from 'constants/config'
import requestCheckEmailAndDomainAssociation, { EmailOrDomainExistsErrorCode, isExistingBusinessAccountLinked } from 'businessTraveller/api/requestCheckEmailAndDomainAssociation'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { authLogout } from 'actions/AuthActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_BUSINESS_AND_EMAIL,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import { formToObject } from 'lib/forms/formToObject'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { sha256 } from 'lib/utils/createHashUtils'
import { logDatadog } from 'services/datadogRum'
import { identifyHotJarUserByEmail } from 'services/hotjar'
import EmailOrDomainExistsMessageBanner from 'businessTraveller/pages/business-signup/components/EmailOrDomainExistsMessageBanner'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { LUXURY_ESCAPES } from 'constants/brands'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const EmailValidationForm = styled.form`
  position: relative;
`

interface EmailValidation {
  businessName: string
  adminEmail: string
}

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'active',
  account_details: 'pending',
  create_password: 'pending',
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps;
}

function BusinessTravellerSignupEmailValidation(props: Props) {
  const { steps } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)
  const [emailOrDomainExistsError, setEmailOrDomainExistsError] = useState<EmailOrDomainExistsErrorCode | null>(null)
  const [existingEmail, setExistingEmail] = useState<string>('')

  const dispatch = useAppDispatch()
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const formRef = useRef<HTMLFormElement>(null)
  const businessEmailRef = useRef<HTMLInputElement>(null)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  const emailCheckMutation = useMutation({
    mutationFn: async(email: string) => {
      const reCaptchaResponse = await handleReCAPTCHA(reCaptchaRef.current)
      return await checkUserExistsForBrand(
        { email },
        reCaptchaResponse,
        isDedicatedTenantRegistrationEnabled ? LUXURY_ESCAPES : undefined,
      )
    },
    onSuccess: async(data, email) => {
      // Check if the email is already associated with a business on LEBT
      lebtBusinessCheckMutation.mutate({ email, leCustomerExists: data?.isExist })
    },
    onError: (error: App.ApiCallFailure) => {
      businessEmailRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const lebtBusinessCheckMutation = useMutation({
    mutationFn: async({ email }: { email: string, leCustomerExists: boolean }) => {
      await requestCheckEmailAndDomainAssociation(email, isDedicatedTenantRegistrationEnabled)
    },
    onSuccess: (_, { leCustomerExists }) => {
      // Email and domain do not exist in LEBT - user can create a new business
      const nextStep = leCustomerExists
        ? BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.LINK_ACCOUNT
        : BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.ACCOUNT_DETAILS
      accountSetupDispatch({
        type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
        step: nextStep,
      })
      setIsFormSubmitting(false)
    },
    onError: (error: App.ApiCallFailure, { email, leCustomerExists }) => {
      // onError case means either the email exists in LEBT or there is a business with same email domain has registered on LEBT
      // Email already exists in LEBT (user could be invited or already activated)
      if (error.message === EmailOrDomainExistsErrorCode.EMAIL_ALREADY_EXISTS) {
        // When dedicated tenant registration is enabled, rely on the authoritative link state
        // (svc-business customerId) returned with the error. Otherwise fall back to the account
        // existence check to preserve the legacy behaviour.
        const isActivatedBusinessUser = isDedicatedTenantRegistrationEnabled
          ? isExistingBusinessAccountLinked(error)
          : leCustomerExists

        if (isActivatedBusinessUser) {
          // Existing activated business user - redirect to login
          accountSetupDispatch({
            type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
            step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.BUSINESS_EXISTS,
          })
        } else {
          // No linked account yet - show banner to activate via invitation email
          setEmailOrDomainExistsError(EmailOrDomainExistsErrorCode.EMAIL_ALREADY_EXISTS)
          setExistingEmail(email)
          logDatadog('Invited user attempted business signup', { emailHash: sha256(email.toLowerCase().trim()) }, 'error')
        }
      // Domain already registered - prevent duplicate business creation
      } else if (error.message === EmailOrDomainExistsErrorCode.DOMAIN_ALREADY_EXISTS) {
        setEmailOrDomainExistsError(EmailOrDomainExistsErrorCode.DOMAIN_ALREADY_EXISTS)
        logDatadog('A new user attempted to signup a business with existent domain', { emailHash: sha256(email.toLowerCase().trim()) }, 'error')
      // Other errors - show inline validation
      } else {
        businessEmailRef.current?.setCustomValidity(error.message)
        formRef.current?.checkValidity()
      }
      setIsFormSubmitting(false)
    },
  })

  const emailOnChange = useCallback(() => {
    businessEmailRef.current?.setCustomValidity('')
    businessEmailRef.current?.reportValidity()
    setEmailOrDomainExistsError(null)
    setExistingEmail('')
  }, [])

  const getEmailInvalidErrorMessage = useCallback(() => {
    if (emailOrDomainExistsError === EmailOrDomainExistsErrorCode.EMAIL_ALREADY_EXISTS) {
      return 'This email is already registered with Business Traveller.'
    }

    if (emailOrDomainExistsError === EmailOrDomainExistsErrorCode.DOMAIN_ALREADY_EXISTS) {
      return 'This email is associated with a business already registered with Business Traveller.'
    }
    return ''
  }, [emailOrDomainExistsError])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    setIsFormSubmitting(true)
    const formData = formToObject<EmailValidation>(event.currentTarget)
    // Capture email in HotJar for session attribution
    identifyHotJarUserByEmail(formData.adminEmail)
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_SET_BUSINESS_AND_EMAIL,
      businessName: formData.businessName,
      adminEmail: formData.adminEmail,
    })
    emailCheckMutation.mutate(formData.adminEmail)
  }, [accountSetupDispatch, emailCheckMutation])

  // Force logout and remove all business data from redux.
  // Not doing this causes the LINK_ACCOUNT component to flash before ACCOUNT_DETAILS.
  useEffect(() => {
    dispatch(authLogout())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SignupContainer>
      <EmailValidationForm onSubmit={handleSubmission} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={24}>
          <Heading variant="heading3">Sign up your business for free</Heading>
          <BusinessTravellerStepper steps={steps} stepStatuses={stepStatuses} />
          {emailOrDomainExistsError && <EmailOrDomainExistsMessageBanner errorCode={emailOrDomainExistsError} email={existingEmail} />}
          <Group direction="vertical" gap={8}>
            <TextInput
              label="Business name"
              maxLength={100}
              minLength={2}
              name="businessName"
              placeholder="Enter your business name"
              pattern="^[A-Za-z0-9 !#$%&'\(\)*,\-.\/:;=?@\\_\{\}\|]+$"
              invalidErrorMessage="Invalid characters in business name."
              required
              requiredErrorMessage="Please enter a business name"
            />
            <TextInput
              type="email"
              label="Business email"
              name="adminEmail"
              placeholder="Enter your business email"
              required
              requiredErrorMessage="Please enter your email address"
              invalidErrorMessage={getEmailInvalidErrorMessage()}
              manualError={!!emailOrDomainExistsError}
              ref={businessEmailRef}
              onChange={emailOnChange}
            />
            <CheckboxInput name="checkbox::boolean" required>
              I confirm that I am an authorised representative of the agency. I have read and accept the <TextLink target="_blank" to="/terms-and-conditions">Terms and Conditions</TextLink> and <TextLink target="_blank" to="/privacy-policy">Privacy Policy</TextLink>
            </CheckboxInput>
          </Group>
          <Group direction="horizontal" horizontalAlign="end">
            <TextButton
              fit="mobile-full-width"
              kind="primary"
              shape="square"
              size="medium"
              type="submit"
              disabled={isFormSubmitting}
            >
              Continue
            </TextButton>
          </Group>
        </Group>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reCaptchaRef} />}
      </EmailValidationForm>
    </SignupContainer>
  )
}

export default BusinessTravellerSignupEmailValidation
